import { useState, useCallback } from "react";
import { debounce } from "lodash";

import PropTypes from "prop-types";
import TelField from "./Input/TelField";
import TextField from "./Input/TextField";
import DateField from "./Input/DateField";
import ChipGroup from "./ChipGroup";
import config from "@/config";
import ClipLoader from "react-spinners/ClipLoader";
import mixpanel from "mixpanel-browser";
import { fetchData } from "@/lib/helper";


export default function DynamicInputForm({
  formConfig,
  formData,
  setFormData,
  onSubmit,
  setError,
  setDisableNextBtn,
}) {
  const [firstName, setFirstName] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit && onSubmit(formData);
  };

  const handleChange = (name, value, maxValue) => {
    if (maxValue && value >= maxValue)
      setError(`The ${name} value exceeds maximum limit`);
    else setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const validatePan = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}/; // PAN structure with 'P' as the 4th character
    return panRegex.test(pan);
  };

  const fetchAddressSuggestions = async (input) => {
    try {
      if (input.length < 3) {
        setAddressSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      const response = await fetch(`${config.API_URL}/user/auto-complete?input=${input}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            config.LOCAL_KEYS.SIGNUP_TOKEN
          )}`,
        },
      });

      let suggestions = await response.json();

      if (Array.isArray(suggestions) && suggestions.length > 0) {
        setAddressSuggestions(suggestions);
        setShowSuggestions(true);
      } else {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Debounce function to avoid excessive API calls
  const debouncedFetchAddressSuggestions = useCallback(
    debounce(fetchAddressSuggestions, 500),
    []
  );

  const fetchPanDetails = async (pan) => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_URL}/qa/pancho/${pan}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            config.LOCAL_KEYS.SIGNUP_TOKEN
          )}`,
        },
      });

      if (!response.ok) {
        setDisableNextBtn(true);
        setFirstName("Error Fetching PAN/Incorrect PAN");
        throw new Error("Failed to fetch PAN details");
      }

      // mixpanel.track(`PAN Fetched successfully`, {
      //   source: "Eligibility Check",
      // });

      let data = await response.json();
      data = data?.data;

      setFirstName(
        data.firstName || data.lastName
          ? `${data.firstName || ""} ${data.lastName || ""}`.trim()
          : "Incorrect PAN"
      );
    } catch (error) {
      console.error("Error fetching PAN details:", error);
  
      setFirstName("Error Fetching PAN/Incorrect PAN");
      setDisableNextBtn(true);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchPanDetails = useCallback(
    debounce(fetchPanDetails, 500),
    []
  );

  const fetchPincodeDetails = async (pincode) => {
    try {
      setPinLoading(true);
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Pincode details");
      }

      const data = await response.json();

      if (data[0]?.Status === "Success" && data[0]?.PostOffice?.[0]?.Block) {
        setCity(data[0].PostOffice[0].Block);
        // mixpanel.track(`Pincode Fetched successfully`, {
        //   source: "Eligibility Check",
        // });
      } else {
        setCity("Invalid Pincode");
      }
    } catch (error) {
      console.error("Error fetching Pincode details:", error);
    
      setCity("Error Fetching Pincode details");
    } finally {
      setPinLoading(false);
    }
  };

  const debouncedFetchPincodeDetails = useCallback(
    debounce(fetchPincodeDetails, 500),
    []
  );

  return (
    <form className="space-y-input-spacing" onSubmit={handleSubmit}>
      {formConfig?.map((field, index) => {
        const value = formData[field.name] || "";
        const handleInputChange = (val) => {
          const capitalizedValue = val.toUpperCase();
          handleChange(field.name, capitalizedValue); // Update formData dynamically
          setFirstName("");

          if (capitalizedValue.length === 10 && field.type === "pan") {
            if (validatePan(capitalizedValue)) {
              debouncedFetchPanDetails(capitalizedValue); // Call the API if PAN is valid
            } else {
              setFirstName("Invalid PAN");
              setDisableNextBtn(true); // Disable the next button for invalid PAN
            }
          }
        };

        switch (field.type) {
          case "autocomplete_address":
          case "address":
            return (
              <div key={index} className="relative">
                <TextField
                  name={field.name}
                  label={field.label}
                  value={value}
                  required={field.required}
                  pattern={field.pattern}
                  icon={field.icon}
                  consent={field?.consent}
                  onChange={(val) => {
                    setFormData((prev) => ({ ...prev, [field.name]: val }));
                    debouncedFetchAddressSuggestions(val);
                  }}
                />
                {console.log(showSuggestions)}
                {/* Address Suggestions Dropdown */}
                {showSuggestions && addressSuggestions.length > 0 && (
                  <ul className="absolute z-10 bg-white border border-gray-300 rounded-md w-full mt-1 shadow-md max-h-48 overflow-y-auto">
                    {console.log(addressSuggestions)}
                    {addressSuggestions.map((suggestion, idx) => (
                      <li
                        key={idx}
                        className={`px-3 py-2 hover:bg-gray-100 cursor-pointer text-black`}
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            [field.name]: suggestion,
                          }));
                          setShowSuggestions(false);
                        }}
                      >
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          case "pan":
            return (
              <div key={index} className="flex flex-col gap-2">
                {loading ? (
                  <div className="w-full flex justify-end gap-2">
                    <ClipLoader color="#1987fb" size={20} />
                    <span className="">Checking PAN</span>
                  </div>
                ) : (
                  <span className="w-full flex justify-end">{firstName}</span>
                )}
                <TextField
                  name={field.name}
                  label={field.label}
                  value={value.toUpperCase()}
                  required={field.required}
                  pattern={field.pattern}
                  icon={field.icon}
                  consent={field?.consent}
                  maxLength={field?.maxLen}
                  inputMode={field?.inputMode || "text"}
                  onChange={handleInputChange}
                />
              </div>
            );
          case "pincode":
            return (
              <div key={index} className="flex flex-col gap-2">
                {pinLoading ? (
                  <div className="w-full flex justify-end gap-2">
                    <ClipLoader color="#1987fb" size={20} />
                    <span>Checking Pincode</span>
                  </div>
                ) : (
                  <span className="w-full flex justify-end">{city}</span>
                )}
                <TextField
                  type="text"
                  key={index}
                  name={field.name}
                  label={field.label}
                  value={value}
                  consent={field?.consent}
                  required={field.required}
                  pattern={field.pattern}
                  icon={field.icon}
                  onChange={(val) => {
                    const numericValue = val.replace(/[^0-9]/g, ""); // Ensure only digits
                    handleChange(field.name, numericValue); // Update formData
                    setCity(""); // Clear city when input changes

                    if (numericValue.length === 6) {
                      debouncedFetchPincodeDetails(numericValue); // Trigger API call for valid pincode
                    }
                  }}
                  maxLength={6}
                  inputMode={field?.inputMode || "numeric"}
                  onKeyPress={(e) => {
                    // Allow only numeric input
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
            );
          case "text":
          case "email":
          case "password":
            return (
              <TextField
                key={index}
                name={field.name}
                label={field.label}
                value={value}
                consent={field?.consent}
                required={field.required}
                pattern={field.pattern}
                icon={field.icon}
                onChange={(val) => handleChange(field.name, val)}
                maxLength={field?.maxLen}
                inputMode={field?.inputMode || "text"}
              />
            );
          case "number":
            return (
              <TextField
                key={index}
                name={field.name}
                label={field.label}
                value={value}
                required={field.required}
                pattern={field.pattern}
                icon={field.icon}
                maxLength={field?.maxLen}
                type="number"
                inputMode="numeric"
                onChange={(val) =>
                  handleChange(
                    field.name,
                    parseInt(val, 10) || 0,
                    field.maxValue
                  )
                }
              />
            );
          case "tel":
            return (
              <TelField
                key={index}
                name={field.name}
                label={field.label}
                value={value}
                required={field.required}
                onChange={(val) => handleChange(field.name, val)}
              />
            );
          case "date":
            return (
              <DateField
                key={index}
                name={field.name}
                label={field.label}
                value={value}
                required={field.required}
                onChange={(val) => handleChange(field.name, val)}
              />
            );
          case "chipgroup":
            return (
              <div
                key={index}
                className="flex flex-col mt-2 space-y-input-spacing"
              >
                {field.label && (
                  <label className="subheading block">{field.label}</label>
                )}
                <ChipGroup
                  options={field.options}
                  selected={value}
                  onChange={(val) => handleChange(field.name, val)}
                  variant={field.variant}
                />
              </div>
            );

          case "dropdown":
            return (
              <div className="relative w-full" key={index}>
                <select
                  name={field.name}
                  value={value}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  required={field.required}
                  className={`w-full bg-input-bg border rounded-md border-primary-500 h-14 shadow-sm px-4 py-2.5 transition-all duration-200 ease-in-out appearance-none pr-10 ${
                    value ? "text-white" : "text-gray-500"
                  } focus:ring-primary-500 focus:border-primary-500`}
                >
                  <option value="" disabled className="text-gray-500">
                    {field.label}
                  </option>
                  {field.options.map((option, optIndex) => (
                    <option
                      key={optIndex}
                      value={option.value}
                      className="text-gray-500"
                    >
                      {option.label || option.value}
                    </option>
                  ))}
                </select>
                <svg
                  className="w-3 h-3 absolute top-1/2 right-4 transform -translate-y-1/2 text-white pointer-events-none"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 10 6"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 4 4 4-4"
                  />
                </svg>
              </div>
            );
          default:
            return null;
        }
      })}
    </form>
  );
}

DynamicInputForm.propTypes = {
  formConfig: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf([
        "pincode",
        "pan",
        "text",
        "email",
        "password",
        "number",
        "date",
        "select",
        "tel",
        "chipgroup",
        "dropdown",
        "address",
        "autocomplete_address",
      ]).isRequired,
      label: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      required: PropTypes.bool,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string,
          heading: PropTypes.string,
          icon: PropTypes.string,
        })
      ),
      pattern: PropTypes.string,
      icon: PropTypes.string,
    })
  ).isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
};
